import React, { useEffect, useRef, useState } from 'react';
import { Play, Video } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal } from '../../../components/ui/Modal';
import placeholder1 from '../../../assets/placeholder1.png';
import placeholder2 from '../../../assets/placeholder2.png';
import { useAuth } from '../../../lib/auth/AuthContext';
import { createCheckoutSession } from '../../../lib/billing/createCheckoutSession';
import { useSubscriptionAccess } from '../../../lib/billing/useSubscriptionAccess';
import { ListenSection } from '../components/ListenSection';

const LAST_QT_KEY = 'newrotune_last_qt';

export const ExploreScreen: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useAuth();
  const { hasFullAccess, profileLoading } = useSubscriptionAccess();
  const [videoModal, setVideoModal] = useState<'qt1' | 'qt2' | null>(null);
  const [checkoutPending, setCheckoutPending] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const modalVideoRef = useRef<HTMLVideoElement>(null);
  const qt2Locked = !profileLoading && !hasFullAccess;

  const qt1Url =
    'https://tfzgakwhkltdrllofhzg.supabase.co/storage/v1/object/sign/videos/video1.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9lMDcyNDU3Ny0yNjdiLTRiYWUtODYxMC00ODQ5MzUxMTRhNjkiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJ2aWRlb3MvdmlkZW8xLm1wNCIsImlhdCI6MTc3MzQxMzQzNywiZXhwIjoxODA0OTQ5NDM3fQ.E37MVPVOqa3WC05tDgecrCEWHInzrvb9ITk0AVOYJWc';
  const qt2Url =
    'https://tfzgakwhkltdrllofhzg.supabase.co/storage/v1/object/sign/videos/video2.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9lMDcyNDU3Ny0yNjdiLTRiYWUtODYxMC00ODQ5MzUxMTRhNjkiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJ2aWRlb3MvdmlkZW8yLm1wNCIsImlhdCI6MTc3MzQxMzgxOCwiZXhwIjoxODA0OTQ5ODE4fQ.dueMDCSkgf5nXr2aFWgn5eQZj9vu69eM9KO6K2XqnM4';

  useEffect(() => {
    const state = location.state as { focusQt?: 'random' | 'last' } | null;
    if (!state?.focusQt) return;

    let targetId: 'qt1' | 'qt2' = 'qt1';
    if (state.focusQt === 'random') {
      targetId = Math.random() < 0.5 ? 'qt1' : 'qt2';
    } else {
      const last = (() => {
        try {
          return localStorage.getItem(LAST_QT_KEY);
        } catch {
          return null;
        }
      })();
      targetId = last === 'qt2' ? 'qt2' : 'qt1';
    }

    requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const markPlayed = (id: 'qt1' | 'qt2') => {
    try {
      localStorage.setItem(LAST_QT_KEY, id);
    } catch {
      /* ignore */
    }
  };

  const closeVideoModal = () => {
    modalVideoRef.current?.pause();
    setVideoModal(null);
  };

  const startCheckout = async () => {
    setCheckoutError(null);
    const token = session?.access_token;
    if (!token) {
      navigate('/auth?redirect=%2Fapp%2Fexplore', { replace: true });
      return;
    }
    const origin = window.location.origin;
    setCheckoutPending(true);
    try {
      const { url } = await createCheckoutSession({
        accessToken: token,
        successUrl: `${origin}/app/profile?checkout=success`,
        cancelUrl: `${origin}/app/explore?checkout=cancel`
      });
      window.location.assign(url);
    } catch (e) {
      setCheckoutError(e instanceof Error ? e.message : 'Could not start checkout.');
      setCheckoutPending(false);
    }
  };

  return (
    <div className="w-full max-w-5xl bg-background px-4 pb-8 pt-6 sm:px-6 lg:mx-auto lg:px-8 lg:pb-10 lg:pt-8">
      <header className="mb-4">
        <h1 className="text-xl font-semibold text-text sm:text-2xl">Explore</h1>
        <p className="mt-1 max-w-2xl text-xs text-textMuted sm:text-sm">
          Enjoy 5–7 minute simple movements for brain change that help you feel calmer, more confident, and more creative—while improving your mental and physical performance.
          Each set activates new brain connections, building new physical and mental patterns. The result? More choices in managing everyday challenges with greater ease.
          They're easy to do anytime, anywhere, sitting or standing. Try one today and discover how a few minutes can transform the way you move, think, and respond to stress.
        </p>
      </header>
      <section className="mt-4" aria-labelledby="explore-watch-heading">
        <div className="space-y-4 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0">
        <div
          id="qt1"
          className="scroll-mt-24 overflow-hidden rounded-3xl border border-borderSoft bg-surface shadow-soft-elevated"
        >
          <div className="px-4 pt-4 pb-2">
            <h3 className="text-sm font-medium text-text">QT1</h3>
            <p className="mt-1 text-xs font-bold text-textMuted">Connect and master emotions, position, breathing.</p>
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => setVideoModal('qt1')}
              className="group relative block w-full overflow-hidden bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
            >
              <span className="relative block aspect-video w-full">
                <img
                  src={placeholder1}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <span
                  className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover:bg-black/35"
                  aria-hidden
                />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-surfaceGlass/95 text-primary shadow-soft-elevated backdrop-blur-sm transition-transform duration-200 ease-soft-spring group-hover:scale-105 group-active:scale-95">
                    <Play className="ml-0.5 h-8 w-8" fill="currentColor" aria-hidden strokeWidth={0} />
                  </span>
                </span>
              </span>
              <span className="sr-only">Play QT1 video</span>
            </button>
          </div>
        </div>

        <div
          id="qt2"
          className="scroll-mt-24 overflow-hidden rounded-3xl border border-borderSoft bg-surface shadow-soft-elevated"
        >
          <div className="px-4 pt-4 pb-2">
            <h3 className="text-sm font-medium text-text">QT2</h3>
            <p className="mt-1 text-xs font-bold text-textMuted">Differentiation, Free genetically-fused patterns and mind.</p>
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                if (qt2Locked) {
                  void startCheckout();
                  return;
                }
                setVideoModal('qt2');
              }}
              disabled={checkoutPending}
              className="group relative block w-full overflow-hidden bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
            >
              <span className="relative block aspect-video w-full">
                <img
                  src={placeholder2}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <span
                  className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover:bg-black/35"
                  aria-hidden
                />
                {qt2Locked ? (
                  <span className="absolute left-3 top-3 rounded-full bg-black/55 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-white">
                    Subscribers only
                  </span>
                ) : null}
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-surfaceGlass/95 text-primary shadow-soft-elevated backdrop-blur-sm transition-transform duration-200 ease-soft-spring group-hover:scale-105 group-active:scale-95">
                    <Play className="ml-0.5 h-8 w-8" fill="currentColor" aria-hidden strokeWidth={0} />
                  </span>
                </span>
              </span>
              <span className="sr-only">Play QT2 video</span>
            </button>
            {checkoutError ? (
              <p className="px-4 py-3 text-xs text-danger" role="alert">
                {checkoutError}
              </p>
            ) : null}
          </div>
        </div>
        </div>
      </section>

      <Modal
        open={videoModal !== null}
        onClose={closeVideoModal}
        title={videoModal === 'qt2' ? 'QT2' : videoModal === 'qt1' ? 'QT1' : undefined}
      >
        {videoModal && (videoModal !== 'qt2' || !qt2Locked) ? (
          <video
            key={videoModal}
            ref={modalVideoRef}
            controls
            autoPlay
            playsInline
            poster={videoModal === 'qt1' ? placeholder1 : placeholder2}
            preload="metadata"
            className="max-h-[min(70vh,520px)] w-full rounded-2xl bg-black"
            onPlay={() => markPlayed(videoModal)}
          >
            <source src={videoModal === 'qt1' ? qt1Url : qt2Url} type="video/mp4" />
            Your device does not support HTML5 video.
          </video>
        ) : null}
      </Modal>

      <ListenSection />
    </div>
  );
};
